import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import {
  Alert,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  CircularProgress,
  Divider as MuiDivider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { Box, spacing } from "@mui/system";
import { format } from "date-fns";
import axiosInstance from "../../utils/axios";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

/**
 * Payments read from the operator's own bank account by the daily job.
 *
 * Both subscription and postal invoices are billed to that one account, so this
 * is one list over one bank feed rather than a page per invoice type — a payment
 * that matches nothing would otherwise have nowhere to appear.
 *
 * The unmatched list is the working one: the job settles anything whose
 * reference and amount agree exactly, and leaves everything else here with the
 * reason it could not. Where it can name the invoice a payment appears to
 * concern, that becomes a button — otherwise the flag is just prose to go
 * hunting from, which is what this replaces.
 */

type Payment = {
  id: string;
  bookingDate: string;
  amountCents: number;
  currency: string;
  payer: string | null;
  referenceNumber: string | null;
  status: string;
  reason: string | null;
  subscriptionInvoiceId: number | null;
  postalInvoiceId: number | null;
  invoiceType: "subscription" | "postal" | null;
  invoiceNumber: string | null;
  invoiceStatus: string | null;
  associationName: string | null;
};

/**
 * Both kinds of invoice are billed to the same bank account, so a payment says
 * which one it settled. A dash means no invoice could be named at all — that is
 * the row a person actually has to look at.
 */
const invoiceTypeLabel = (payment: Payment) => {
  if (payment.invoiceType === "postal") return "Posti";
  if (payment.invoiceType === "subscription") return "Tilaus";
  return "—";
};

/** The endpoint that settles this payment's invoice. */
const markPaidUrl = (payment: Payment) =>
  payment.invoiceType === "postal"
    ? `/postal-invoices/${payment.postalInvoiceId}/mark-paid`
    : `/subscription-invoices/${payment.subscriptionInvoiceId}/mark-paid`;

const invoiceIdOf = (payment: Payment) =>
  payment.invoiceType === "postal"
    ? payment.postalInvoiceId
    : payment.subscriptionInvoiceId;

const eur = (cents: number) =>
  new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
  }).format((cents ?? 0) / 100);

function SuperadminSubscriptionPayments() {
  const [tab, setTab] = useState<"unmatched" | "matched">("unmatched");
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [running, setRunning] = useState(false);

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    progress,
    duration,
  } = useSnackbar();

  const notify = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/subscription-payments", {
        params: { status: tab },
      });
      setRows(data ?? []);
    } catch (error: any) {
      notify(
        error?.response?.data?.message ?? "Maksujen haku epäonnistui.",
        "error"
      );
    } finally {
      setLoading(false);
    }
    // notify is stable enough for this page; re-running on tab is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Runs the same job the 06:00 UTC schedule fires. It is queued on the worker
   * rather than run here, so the list is reloaded a moment later rather than
   * immediately — the run takes a few seconds against the bank.
   */
  const runNow = async () => {
    setRunning(true);
    try {
      await axiosInstance.post("/subscription-payments/run");
      notify(
        "Täsmäytys käynnistetty. Listaus päivittyy hetken kuluttua.",
        "success"
      );
      window.setTimeout(() => {
        load();
        setRunning(false);
      }, 8000);
    } catch (error: any) {
      notify(
        error?.response?.data?.message ??
          "Täsmäytyksen käynnistys epäonnistui.",
        "error"
      );
      setRunning(false);
    }
  };

  /**
   * Settling goes through the same endpoint a person uses by hand, so there is
   * one code path per invoice type rather than two that can drift.
   */
  const markPaid = async (payment: Payment) => {
    const invoiceId = invoiceIdOf(payment);
    if (!invoiceId) return;
    // Only a subscription payment moves a subscription period; a postal one is
    // a plain status flip, so promising reactivation there would be a lie.
    const consequence =
      payment.invoiceType === "postal"
        ? "Postilasku merkitään maksetuksi."
        : "Tilauskausi siirtyy eteenpäin ja tilaus aktivoituu.";
    if (
      !window.confirm(
        `Merkitäänkö lasku ${
          payment.invoiceNumber ?? invoiceId
        } maksetuksi?\n\n` + consequence
      )
    ) {
      return;
    }
    setBusyId(invoiceId);
    try {
      await axiosInstance.put(markPaidUrl(payment));
      notify("Lasku merkitty maksetuksi.", "success");
      await load();
    } catch (error: any) {
      notify(
        error?.response?.data?.message ?? "Laskun merkitseminen epäonnistui.",
        "error"
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <React.Fragment>
      <Helmet title="Saapuneet maksut" />
      <Typography variant="h3" gutterBottom display="inline">
        Saapuneet maksut
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Saapuneet maksut</Typography>
      </Breadcrumbs>
      <Divider my={6} />

      <Card mb={6}>
        <CardContent>
          <Alert severity="info" sx={{ mb: 4 }}>
            Pankkitilin tapahtumat haetaan kerran vuorokaudessa. Maksut, joiden
            viitenumero ja summa täsmäävät avoimeen laskuun, merkitään
            maksetuiksi automaattisesti. Loput näkyvät tässä syineen.
          </Alert>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
            }}
          >
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              <Tab label="Selvitettävät" value="unmatched" />
              <Tab label="Kohdistetut" value="matched" />
            </Tabs>
            <Button
              variant="outlined"
              size="small"
              disabled={running}
              onClick={runNow}
            >
              {running ? "Haetaan…" : "Hae tapahtumat nyt"}
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Alert severity="success">
              {tab === "unmatched"
                ? "Ei selvitettäviä maksuja."
                : "Ei vielä kohdistettuja maksuja."}
            </Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pvm</TableCell>
                    <TableCell>Maksaja</TableCell>
                    <TableCell align="right">Summa</TableCell>
                    <TableCell>Viite</TableCell>
                    <TableCell>Laskutyyppi</TableCell>
                    <TableCell>
                      {tab === "unmatched" ? "Syy" : "Lasku"}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        {format(new Date(payment.bookingDate), "dd.MM.yyyy")}
                      </TableCell>
                      <TableCell>{payment.payer ?? "—"}</TableCell>
                      <TableCell align="right">
                        {eur(payment.amountCents)}
                      </TableCell>
                      <TableCell>{payment.referenceNumber ?? "—"}</TableCell>
                      <TableCell>{invoiceTypeLabel(payment)}</TableCell>
                      <TableCell>
                        {tab === "unmatched" ? (
                          payment.reason
                        ) : (
                          <>
                            {payment.invoiceNumber ?? "—"}
                            {payment.associationName ? (
                              <Typography variant="caption" display="block">
                                {payment.associationName}
                              </Typography>
                            ) : null}
                          </>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {tab === "unmatched" &&
                        invoiceIdOf(payment) &&
                        payment.invoiceStatus !== "paid" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busyId === invoiceIdOf(payment)}
                            onClick={() => markPaid(payment)}
                          >
                            Merkitse lasku maksetuksi
                          </Button>
                        ) : tab === "matched" ? (
                          <Chip size="small" label="Maksettu" color="success" />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </CardContent>
      </Card>

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={() => setOpenSnackbar(false)}
      />
    </React.Fragment>
  );
}

export default SuperadminSubscriptionPayments;
